import { supabase } from '../lib/supabase';

/**
 * Payment Service
 * Handles all Stripe payment operations through Supabase Edge Functions
 */

export const paymentService = {
  /**
   * Creates a payment intent for the subscription
   */
  async createPaymentIntent(orderData, customerInfo) {
    try {
      // Validate required data
      if (!orderData || !orderData?.total || orderData?.total <= 0) {
        throw new Error('Invalid payment amount');
      }

      if (!customerInfo || !customerInfo?.email) {
        throw new Error('Customer email is required');
      }

      console.log('Calling create-payment-intent edge function...', { orderData, customerInfo });

      // Call Supabase Edge Function
      const { data, error } = await supabase?.functions?.invoke('create-payment-intent', {
        body: {
          orderData,
          customerInfo
        }
      });

      if (error) {
        console.error('Payment intent creation error:', error);
        throw new Error(error.message || 'Failed to create payment intent');
      }

      if (!data || !data?.clientSecret) {
        console.error('Invalid response from payment service:', data);
        throw new Error('Invalid response from payment service - missing client secret');
      }

      console.log('Payment intent created successfully:', data);

      return {
        clientSecret: data?.clientSecret,
        paymentIntentId: data?.paymentIntentId,
        orderId: data?.orderId,
        orderNumber: data?.orderNumber
      };
    } catch (error) {
      console.error('Create payment intent failed:', error);
      throw error;
    }
  },

  /**
   * Confirms the payment after Stripe processes it
   */
  async confirmPayment(paymentIntentId) {
    try {
      if (!paymentIntentId) {
        throw new Error('Payment intent ID is required');
      }

      console.log('Confirming payment...', paymentIntentId);

      const { data, error } = await supabase?.functions?.invoke('confirm-payment', {
        body: { paymentIntentId }
      });

      if (error) {
        console.error('Payment confirmation error:', error);
        // Return partial success if payment went through but confirmation failed
        return {
          success: false,
          warning: 'Payment was processed but confirmation failed. Please contact support.',
          error: error?.message
        };
      }

      console.log('Payment confirmed successfully:', data);

      return {
        success: true,
        data: data
      };
    } catch (error) {
      console.error('Confirm payment failed:', error);
      return {
        success: false,
        warning: 'Payment was processed but confirmation failed. Please contact support.',
        error: error?.message
      };
    }
  },

  /**
   * Formats amount for display
   */
  formatAmount(amount, currency = 'USD') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    })?.format(amount);
  }
};